import { inject, Injector, REQUEST, REQUEST_CONTEXT, RESPONSE_INIT, Type } from '@angular/core';
import { ApiUser } from '@tytapp/api';
import type * as express from 'express';
import { v4 as uuid } from 'uuid';
import { LoggerService } from './logger.service';

export class TYTRequestContext {
    constructor(
        readonly request: Omit<express.Request, 'body'>,
        readonly response: express.Response,
    ) {
    }

    readonly logger = this.createLogger();
    static BadRequest = class extends Error {};

    readonly requestId: string = uuid();

    apmServiceClass: unknown;
    redirection: { url: string } = { url: undefined };

    private _ngRequest: Request;
    ngResponseInit: ResponseInit;

    get ngRequest(): Omit<Request, 'body'> {
        return this._ngRequest;
    }

    set ngRequest(value: Request) {
        this._ngRequest = value;
    }

    get method() { return this.request.method; }
    get url() { return this.request.url; }
    get path() {
        try {
            return new URL(this.request.url).pathname;
        } catch (e) {
            this.logger.error(`[FormHandler/SSR] Error: Failed to parse current URL '${this.request.url}' for pathname: ${e.stack || e.message || e}`);
            return '/';
        }
    }

    get bodyType() { return this.request.headers['content-type']; }

    private _body: any = undefined;
    async body(): Promise<Record<string, any>> {
        if (this._body)
            return this._body;

        if (this.bodyType === 'application/json') {
            this._body = await this.ngRequest.json();
        } else if (this.bodyType === 'application/x-www-form-urlencoded') {
            let formData: FormData = await this.ngRequest.formData();
            let body = {};

            formData.forEach((value, key) => {
                body[key] = value;
            });

            this._body = body;
        } else {
            this.logger.error(`Unknown body mimetype '${this.bodyType}': serving Bad Request`);
            throw new TYTRequestContext.BadRequest();
        }

        return this._body;
    }

    user: ApiUser;

    private createLogger() {
        return Injector.create(
            {
                providers: [
                    { provide: REQUEST_CONTEXT, useValue: this },
                    LoggerService
                ]
            }
        ).get(LoggerService);
    }

}

/**
 * Retrieve the low-level request context. This can be used to interact directly
 * with Express when running on the server (bypassing Angular SSR's own facilities).
 * @returns
 */
export function injectRequestContext() {
    let context = inject(REQUEST_CONTEXT, { optional: true }) as TYTRequestContext;
    if (context) {
        context.ngRequest = inject(REQUEST);
        context.ngResponseInit = inject(RESPONSE_INIT);
    }
    return context;
}

export function injectRequest() {
    const context = injectRequestContext();
    return context?.request;
}

export function injectResponse() {
    const context = injectRequestContext();
    return context?.response;
}